import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  Alert,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Divider
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { BRAND } from '../../constants/branding';
import TenantHubLogo from '../../components/TenantHubLogo';

const TenantSignup = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [catalog, setCatalog] = useState({ applications: [], services: [], plans: [] });
  const [form, setForm] = useState({
    company_name: '',
    company_address: '',
    contact_email: '',
    contact_name: '',
    contact_phone: '',
    website: '',
    notes: '',
    selected_applications: [],
    selected_services: [],
    selected_plan_id: ''
  });

  useEffect(() => {
    api.get('/catalog/public/signup-catalog')
      .then((res) => {
        const data = res.data?.data || { applications: [], services: [], plans: [] };
        setCatalog(data);
        if (data.plans?.length) {
          setForm((prev) => ({ ...prev, selected_plan_id: data.plans[0].id }));
        }
      })
      .catch(() => toast.error('Failed to load product catalog'))
      .finally(() => setLoading(false));
  }, []);

  const toggleId = (field, id) => {
    setForm((prev) => {
      const set = new Set(prev[field]);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, [field]: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_email) {
      toast.error('Company name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/catalog/public/signup', {
        ...form,
        selected_plan_id: form.selected_plan_id || null
      });
      setSubmitted(true);
      toast.success('Signup request submitted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 45%, #0f766e 100%)',
        py: 4,
        px: 2
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, color: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TenantHubLogo size={52} gradient="linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)" />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                {BRAND.productName}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {BRAND.catchPhrase}
              </Typography>
            </Box>
          </Box>
          <Button component={RouterLink} to="/login" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>
            Admin Login
          </Button>
        </Box>

        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2.5, background: 'linear-gradient(90deg, #0ea5e9 0%, #14b8a6 100%)', color: '#fff' }}>
            <Typography variant="h5" fontWeight={700}>Tenant Signup</Typography>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              Tell us about your company, pick the software & services you need, and choose a plan.
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {submitted ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Thanks! Your signup request was received. Our administrators will review it and contact you at the email you provided.
              </Alert>
            ) : (
              <form onSubmit={handleSubmit}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Company Details</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Company Name"
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Email"
                      value={form.contact_email}
                      onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Company Address"
                      value={form.company_address}
                      onChange={(e) => setForm({ ...form, company_address: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Contact Name"
                      value={form.contact_name}
                      onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={form.contact_phone}
                      onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Website"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" fontWeight={700} gutterBottom>Software Applications</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Select the products you want to run as a tenant.
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {catalog.applications.map((app) => (
                    <Grid item xs={12} md={6} key={app.id}>
                      <Card variant="outlined" sx={{ p: 1.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={form.selected_applications.includes(app.id)}
                              onChange={() => toggleId('selected_applications', app.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography fontWeight={600}>{app.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{app.description}</Typography>
                              {app.category && <Chip size="small" label={app.category} sx={{ ml: 1 }} />}
                            </Box>
                          }
                        />
                      </Card>
                    </Grid>
                  ))}
                  {!catalog.applications.length && (
                    <Grid item xs={12}><Alert severity="info">No applications published yet.</Alert></Grid>
                  )}
                </Grid>

                <Typography variant="h6" fontWeight={700} gutterBottom>Services / Framework</Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {catalog.services.map((svc) => (
                    <Grid item xs={12} md={6} key={svc.id}>
                      <Card variant="outlined" sx={{ p: 1.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={form.selected_services.includes(svc.id)}
                              onChange={() => toggleId('selected_services', svc.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography fontWeight={600}>{svc.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{svc.description}</Typography>
                              <Chip size="small" label={svc.type} sx={{ ml: 1 }} />
                            </Box>
                          }
                        />
                      </Card>
                    </Grid>
                  ))}
                  {!catalog.services.length && (
                    <Grid item xs={12}><Alert severity="info">No services published yet.</Alert></Grid>
                  )}
                </Grid>

                <Typography variant="h6" fontWeight={700} gutterBottom>Choose a Plan</Typography>
                <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
                  <FormLabel component="legend">Pricing</FormLabel>
                  <RadioGroup
                    value={form.selected_plan_id}
                    onChange={(e) => setForm({ ...form, selected_plan_id: e.target.value })}
                  >
                    {catalog.plans.map((plan) => (
                      <Card key={plan.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <FormControlLabel
                          value={plan.id}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography fontWeight={700}>
                                {plan.name}{' '}
                                {plan.is_free ? (
                                  <Chip size="small" color="success" label="Free" />
                                ) : (
                                  <Chip
                                    size="small"
                                    color="primary"
                                    label={`${plan.currency} ${Number(plan.final_price).toFixed(2)} / ${plan.billing_period}`}
                                  />
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">{plan.description}</Typography>
                            </Box>
                          }
                        />
                      </Card>
                    ))}
                  </RadioGroup>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Additional notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  sx={{ mb: 3 }}
                />

                <Button type="submit" variant="contained" size="large" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} color="inherit" /> : 'Submit Signup Request'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default TenantSignup;
